import {
  PreferenceStoreAuthLevel,
  PrivacyCenterTrackingPurposePreview,
} from '@main/cm-types';
import {
  addQueryParams,
  buildUseMutation,
  Form,
  formatErrorMessage,
  message,
  Skeleton,
  useForm,
  useRouter,
  Validators,
} from '@main/core-ui';
import { A } from '@main/pc-core-components';
import { endpoints } from '@main/pc-types';
import {
  Card,
  EmailFormInput,
  RequiredEnabledAirgap,
  StyledPrimaryButton,
  StyledRowContainer,
} from '@main/privacy-center/src/components';
import { selectSombraPublicKey } from '@main/privacy-center/src/containers/App/selectors';
import {
  useLoginRedirection,
  usePartition,
} from '@main/privacy-center/src/hooks';
import {
  createDhEncryptionKeys,
  createDhPayloadHelpers,
} from '@main/sm-browser';
import {
  REFER_USER_TRUSTED_PAYLOAD,
  SombraDataSubjectAuthMethod,
} from '@main/sombra-types';
import { sp } from '@main/theme';
import { LanguageKey } from '@transcend-io/internationalization';
import { Switch } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Purpose } from '../../../CommunicationsPreferences/components';
import { privacySettingMessages } from './messages';
import { OptOutSuccess } from './wrappers';

const useUnauthenticatedPreferenceUpdate = buildUseMutation(
  endpoints.unauthenticatedPreferenceUpdate,
);

export interface UnauthenticatedOptOutFormProps {
  /** current Consent selections */
  consent: Record<string, Purpose>;
  /** Subject Type */
  subjectType: string;
  /** Callback to handle the change of a consent preference */
  onChange: (consent: Record<string, Purpose>) => void;
  /** Purpose configuration */
  configuredPurpose: PrivacyCenterTrackingPurposePreview;
}

/**
 * Component to Opt Out of a privacy preference for UnAuthenticated users.
 * TODO: https://transcend.height.app/T-39129 - Refactor in a better way
 * TODO: https://transcend.height.app/T-19886 - remove ant
 */
export const UnauthenticatedOptOutForm: React.FC<
  UnauthenticatedOptOutFormProps
> = ({ consent, subjectType, configuredPurpose, onChange }) => {
  const theme = useTheme();
  const { locale, formatMessage } = useIntl();
  const { location, redirect } = useRouter();
  const [{ selectedPartition, partitionOptions }] = usePartition();
  const sombraPublicKey = useSelector(selectSombraPublicKey);
  const [status, setStatus] = useState(false);
  const redirectTo = useLoginRedirection();
  redirectTo({ key: 'redirectTo', value: 'log-in' }, 'modal=log-in');

  const formMethods = useForm({
    shouldUnregister: false,
    defaultValues: {
      email: '',
    },
  });

  const [title, displayDescription] = useMemo(
    () => [
      formatMessage(configuredPurpose.title),
      configuredPurpose.displayDescription
        ? formatMessage(configuredPurpose.displayDescription)
        : '',
    ],
    [configuredPurpose, formatMessage],
  );

  const [updateUnauthenticatedPreferenceUpdate, { loading, error }] =
    useUnauthenticatedPreferenceUpdate();

  useEffect(() => {
    if (error) {
      message.error(formatErrorMessage(error.message));
    }
  }, [error]);

  const handleOptOutSubmit = async (data: {
    /** email address */
    email: string;
  }): Promise<void> => {
    if (!sombraPublicKey) return;
    const consentPreference = {
      partition: selectedPartition!,
      purposes: [
        {
          purpose: configuredPurpose.trackingType,
          consent: false,
        },
      ],
      identifier: REFER_USER_TRUSTED_PAYLOAD,
    };
    const dhEncryptionKeys = createDhEncryptionKeys(sombraPublicKey);
    const [createDhPayload] = createDhPayloadHelpers<true>({
      authMethod: SombraDataSubjectAuthMethod.NoAuth,
      authSecret: 'none',
      dhEncryptionKeys,
    });
    await updateUnauthenticatedPreferenceUpdate({
      variables: {
        input: {
          consentPreferences: [consentPreference],
          subjectType,
          locale: locale as LanguageKey,
        },
        dhEncrypted: createDhPayload({
          identifiersToEncrypt: [
            [
              {
                name: 'email',
                value: data.email,
              },
            ],
          ],
        }),
      },
    }).then(({ data }) => {
      if (data) {
        setStatus(true);
      }
    });
  };

  // cannot render without a partition
  if (partitionOptions.length === 0) {
    return <RequiredEnabledAirgap />;
  }
  if (!selectedPartition) {
    return <Skeleton />;
  }

  return (
    <Card
      title={title}
      description={displayDescription}
      titleExtras={
        <Switch
          style={
            consent[configuredPurpose.trackingType]?.consent
              ? {
                  backgroundColor: theme.colors.primary,
                }
              : {}
          }
          checked={consent[configuredPurpose.trackingType]?.consent}
          onChange={(checked) => {
            onChange({
              ...consent,
              [configuredPurpose.trackingType]: {
                consent: checked,
              },
            });
          }}
        />
      }
    >
      {consent[configuredPurpose.trackingType]?.consent === false &&
      configuredPurpose.authLevel === PreferenceStoreAuthLevel.AllowOptOut ? (
        <StyledRowContainer>
          <p
            style={{
              color: theme.colors.textLightOnBg,
              margin: 0,
              width: '50%',
            }}
          >
            {formatMessage(privacySettingMessages.emailOptOutDescription)}
          </p>
          <Form
            useFormMethods={formMethods}
            onSubmit={(data) => {
              handleOptOutSubmit(data);
            }}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: sp(1.5),
              marginLeft: 'auto',
            }}
          >
            <EmailFormInput
              name="email"
              rules={[Validators.REQUIRED, Validators.EMAIL]}
            />
            {status ? (
              <OptOutSuccess>
                {formatMessage(privacySettingMessages.optOutSuccess)}
              </OptOutSuccess>
            ) : (
              <StyledPrimaryButton
                buttonType="primary"
                type="submit"
                loading={loading}
                style={{
                  minWidth: sp(10),
                }}
              >
                {formatMessage(privacySettingMessages.optOut)}
              </StyledPrimaryButton>
            )}
          </Form>
        </StyledRowContainer>
      ) : (
        <p
          style={{
            color: theme.colors.textLightOnBg,
            margin: 0,
          }}
        >
          {formatMessage(privacySettingMessages.unauthenticatedUpdatesWarning, {
            logIn: (
              <A
                type="secondary"
                onClick={() =>
                  redirect(
                    addQueryParams(location, {
                      modal: 'select-subject',
                      redirectTo: `${location.pathname}?redirectTo=log-in`,
                    }),
                  )
                }
              >
                {formatMessage(privacySettingMessages.logIn)}
              </A>
            ),
          })}
        </p>
      )}
    </Card>
  );
};
